import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion } from "../lib/motion"

// Location popups' hero: each time the popup shows, the tile pops in, the pin drops onto the plaza with a bounce and
// a squash, the rings start (CSS, .is-landed) and the pin floats. Stops while hidden; reduced motion shows it landed.
export default class extends Controller {
  static targets = [ "tile", "pin", "shadow" ]

  connect() {
    this.observer = new IntersectionObserver(([ entry ]) => {
      if (entry.isIntersecting) this.play()
      else this.reset()
    }, { threshold: 0.2 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.reset()
  }

  async play() {
    if (this.playing) return
    this.playing = true
    if (prefersReducedMotion()) {
      this.element.classList.add("is-landed")
      return
    }

    const { default: gsap } = await import("gsap")
    if (!this.playing) return
    this.gsap = gsap
    const tl = gsap.timeline()
    tl.fromTo(this.tileTarget, { opacity: 0, y: 18, scale: 0.9 },
      { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.5)" })
    tl.fromTo(this.pinTarget, { opacity: 0, y: -130 },
      { opacity: 1, y: 0, duration: 0.7, ease: "bounce.out" }, 0.25)
    tl.fromTo(this.shadowTarget, { opacity: 0, scale: 0.3 },
      { opacity: 1, scale: 1, duration: 0.7, ease: "power2.in" }, 0.25)
    // Squash on the first touch-down, then settle
    tl.to(this.pinTarget, { scaleY: 0.86, scaleX: 1.1, duration: 0.09, ease: "power1.out" }, 0.6)
    tl.to(this.pinTarget, { scaleY: 1, scaleX: 1, duration: 0.35, ease: "elastic.out(1, 0.45)" }, 0.69)
    tl.add(() => {
      this.element.classList.add("is-landed")
      this.startFloat()
    }, 1.0)
    this.timeline = tl
  }

  // A verification succeeded while this hero is showing: the pin hops and lands again and the rings burst green
  async celebrate() {
    if (!this.playing) return
    this.element.classList.add("is-verified")
    this.element.classList.remove("is-landed")
    void this.element.offsetWidth // restart the ring animation from the plaza
    this.element.classList.add("is-landed")
    if (prefersReducedMotion()) return

    const gsap = this.gsap || (await import("gsap")).default
    this.gsap = gsap
    this.float?.kill()
    this.timeline?.kill()
    this.timeline = gsap.timeline({ onComplete: () => this.startFloat() })
      .to(this.pinTarget, { y: -30, scaleX: 1, scaleY: 1, duration: 0.24, ease: "power2.out" })
      .to(this.shadowTarget, { scale: 0.55, opacity: 0.5, duration: 0.26, ease: "power2.out" }, 0)
      .to(this.pinTarget, { y: 0, duration: 0.42, ease: "bounce.out" })
      .to(this.shadowTarget, { scale: 1, opacity: 1, duration: 0.42, ease: "power2.in" }, "<")
      .to(this.pinTarget, { scaleY: 0.86, scaleX: 1.1, duration: 0.08, ease: "power1.out" }, "-=0.3")
      .to(this.pinTarget, { scaleY: 1, scaleX: 1, duration: 0.35, ease: "elastic.out(1, 0.45)" })
  }

  startFloat() {
    this.float = this.gsap.timeline({ repeat: -1, yoyo: true, defaults: { duration: 1.4, ease: "sine.inOut" } })
      .to(this.pinTarget, { y: -7 }, 0)
      .to(this.shadowTarget, { scale: 0.82, opacity: 0.7 }, 0)
  }

  reset() {
    this.playing = false
    this.timeline?.kill()
    this.float?.kill()
    this.element.classList.remove("is-landed", "is-verified")
    if (this.gsap) this.gsap.set([ this.tileTarget, this.pinTarget, this.shadowTarget ], { clearProps: "all" })
  }
}
